import React, { useMemo } from "react";
import { alpha } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import LocalAtmOutlinedIcon from "@mui/icons-material/LocalAtmOutlined";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import BPePaymentPanel from "./BPePaymentPanel";
import BPeRedeCardForm from "./BPeRedeCardForm";
import PreReservaTimer from "./PreReservaTimer";
import { BPE_PAYMENT_TIPO, isRedeCardPayment } from "../../api/bpeApi";

const LINK_TIPOS = [
  BPE_PAYMENT_TIPO.CIELO,
  BPE_PAYMENT_TIPO.MERCADO_PAGO,
  BPE_PAYMENT_TIPO.XPAY,
  BPE_PAYMENT_TIPO.APPMAX,
];

function paymentSuffix(pm, t) {
  const tipo = Number(pm.tipo);
  if (tipo === BPE_PAYMENT_TIPO.CASH) return t("bpeWizard.paymentCash");
  if (tipo === BPE_PAYMENT_TIPO.PIX_ITAU) return t("bpeWizard.paymentPix");
  if (LINK_TIPOS.includes(tipo)) return t("bpeWizard.paymentLink");
  return null;
}

/** Ícone + descrição curta por tipo de forma de pagamento. */
function paymentVisual(pm, t) {
  const tipo = Number(pm.tipo);
  if (tipo === BPE_PAYMENT_TIPO.CASH) {
    return { Icon: LocalAtmOutlinedIcon, desc: t("bpeWizard.payInstant"), instant: true };
  }
  if (tipo === BPE_PAYMENT_TIPO.PIX_ITAU) {
    return { Icon: QrCode2Icon, desc: t("bpeWizard.payViaPix"), instant: false };
  }
  if (tipo === BPE_PAYMENT_TIPO.REDE) {
    return { Icon: CreditCardOutlinedIcon, desc: t("bpeWizard.payViaCard"), instant: false };
  }
  if (LINK_TIPOS.includes(tipo)) {
    return { Icon: LinkOutlinedIcon, desc: t("bpeWizard.payViaLink"), instant: false };
  }
  return { Icon: PaymentsOutlinedIcon, desc: "", instant: false };
}

function parseMoney(v) {
  if (v == null || v === "") return 0;
  const n = Number(String(v).replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/** Percentual positivo (aceita string com vírgula); 0 quando inválido/ausente. */
function parsePercent(v) {
  if (v == null) return 0;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Passo de pagamento — resumo do valor + seleção da forma de pagamento como cartões.
 */
export default function BPePaymentStep({
  t,
  paymentMethods,
  selectedPaymentId,
  onSelectPayment,
  selectedPayment,
  selectedTrip,
  formatPrice,
  paymentPending,
  paymentCharge,
  preReservaExpiresAt,
  loading,
  conversationId,
  onPaid,
  onSendToChat,
  redeCard,
  onRedeCardChange,
  passengerCount = 1,
  roundTrip = false,
  outboundTrip = null,
  outboundSummary = "",
  returnSummary = "",
  gratuityFareTotal = null,
}) {
  const qtd = Math.max(1, Number(passengerCount) || 1);

  const breakdown = useMemo(() => {
    const idaUnit = roundTrip ? parseMoney(outboundTrip?.valor) : 0;
    const mainUnit = parseMoney(selectedTrip?.valor);
    if (roundTrip) {
      const idaTotal = idaUnit * qtd;
      const voltaTotal = mainUnit * qtd;
      return { unit: mainUnit, idaUnit, voltaUnit: mainUnit, idaTotal, voltaTotal, fareTotal: idaTotal + voltaTotal };
    }
    return { unit: mainUnit, idaUnit: 0, voltaUnit: 0, idaTotal: 0, voltaTotal: 0, fareTotal: mainUnit * qtd };
  }, [roundTrip, outboundTrip, selectedTrip, qtd]);

  // Total efetivamente cobrado = tarifa × passageiros + acréscimo − desconto da forma escolhida.
  // Mesma fórmula do backend (bpeWebCheckoutService.applyPaymentMethodAdjustment) para o
  // valor exibido bater com o valor da cobrança/link.
  const charge = useMemo(() => {
    const fareTotal =
      gratuityFareTotal != null && Number.isFinite(Number(gratuityFareTotal))
        ? round2(Number(gratuityFareTotal))
        : round2(breakdown.fareTotal);
    const acrescimoPct = parsePercent(selectedPayment?.acrescimo);
    const descontoPct = parsePercent(selectedPayment?.desconto);
    const acrescimoValor = round2(fareTotal * (acrescimoPct / 100));
    const descontoValor = round2(fareTotal * (descontoPct / 100));
    const total = Math.max(0, round2(fareTotal + acrescimoValor - descontoValor));
    return { fareTotal, acrescimoPct, acrescimoValor, descontoPct, descontoValor, total };
  }, [breakdown.fareTotal, selectedPayment, gratuityFareTotal]);

  if (paymentPending) {
    return (
      <Box>
        {preReservaExpiresAt && <PreReservaTimer expiresAt={preReservaExpiresAt} />}
        <BPePaymentPanel
          conversationId={conversationId}
          chargeData={paymentCharge}
          paymentMethodId={selectedPaymentId}
          onPaid={onPaid}
          onSendToChat={onSendToChat}
          autoEmitOnPaid
        />
      </Box>
    );
  }

  const perPassengerLabel =
    qtd > 1 ? `${formatPrice(breakdown.unit)} × ${t("bpeWizard.passengersN", { count: qtd })}` : null;
  const hasAdjustment = charge.acrescimoValor > 0 || charge.descontoValor > 0;

  return (
    <Box>
      {/* Resumo do valor a cobrar — bloco de destaque com detalhamento */}
      {charge.fareTotal > 0 && (
        <Paper
          elevation={0}
          sx={(theme) => ({
            p: 1.25,
            borderRadius: 2.5,
            mb: 1.25,
            color: theme.palette.primary.contrastText,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: `0 8px 24px -14px ${alpha(theme.palette.primary.main, 0.7)}`,
          })}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 600, letterSpacing: 0.3 }}>
                {t("bpeWizard.totalToCharge")}
              </Typography>
              <Typography
                fontWeight={800}
                sx={{ lineHeight: 1.05, fontSize: { xs: "1.55rem", sm: "1.8rem" } }}
              >
                {formatPrice(charge.total)}
              </Typography>
              {(perPassengerLabel || roundTrip) && (
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  {roundTrip ? t("bpeWizard.combinedTotal") : perPassengerLabel}
                </Typography>
              )}
            </Box>
            <PaymentsOutlinedIcon sx={{ fontSize: 30, opacity: 0.45, flexShrink: 0 }} />
          </Stack>

          {/* Detalhamento: composição do total (trechos / tarifa + ajustes). */}
          {(roundTrip || hasAdjustment) && (
            <Stack
              spacing={0.25}
              sx={{ mt: 1, pt: 1, borderTop: (theme) => `1px solid ${alpha(theme.palette.primary.contrastText, 0.25)}` }}
            >
              {roundTrip ? (
                <>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Typography variant="caption" sx={{ opacity: 0.9, minWidth: 0 }} noWrap>
                      {t("bpeWizard.legIda")}
                      {outboundSummary ? ` · ${outboundSummary}` : ""}
                      {qtd > 1 ? ` (${formatPrice(breakdown.idaUnit)} × ${qtd})` : ""}
                    </Typography>
                    <Typography variant="caption" fontWeight={700} sx={{ flexShrink: 0 }}>
                      {formatPrice(breakdown.idaTotal)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Typography variant="caption" sx={{ opacity: 0.9, minWidth: 0 }} noWrap>
                      {t("bpeWizard.legVolta")}
                      {returnSummary ? ` · ${returnSummary}` : ""}
                      {qtd > 1 ? ` (${formatPrice(breakdown.voltaUnit)} × ${qtd})` : ""}
                    </Typography>
                    <Typography variant="caption" fontWeight={700} sx={{ flexShrink: 0 }}>
                      {formatPrice(breakdown.voltaTotal)}
                    </Typography>
                  </Stack>
                </>
              ) : (
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Typography variant="caption" sx={{ opacity: 0.9, minWidth: 0 }} noWrap>
                    {t("bpeWizard.fareSubtotal")}
                    {qtd > 1 ? ` (${formatPrice(breakdown.unit)} × ${qtd})` : ""}
                  </Typography>
                  <Typography variant="caption" fontWeight={700} sx={{ flexShrink: 0 }}>
                    {formatPrice(charge.fareTotal)}
                  </Typography>
                </Stack>
              )}

              {roundTrip && hasAdjustment && (
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Typography variant="caption" sx={{ opacity: 0.9, minWidth: 0 }} noWrap>
                    {t("bpeWizard.fareSubtotal")}
                  </Typography>
                  <Typography variant="caption" fontWeight={700} sx={{ flexShrink: 0 }}>
                    {formatPrice(charge.fareTotal)}
                  </Typography>
                </Stack>
              )}

              {charge.acrescimoValor > 0 && (
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Typography variant="caption" sx={{ opacity: 0.9, minWidth: 0 }} noWrap>
                    {t("bpeWizard.surchargeLine", { pct: charge.acrescimoPct })}
                  </Typography>
                  <Typography variant="caption" fontWeight={700} sx={{ flexShrink: 0 }}>
                    {`+ ${formatPrice(charge.acrescimoValor)}`}
                  </Typography>
                </Stack>
              )}

              {charge.descontoValor > 0 && (
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Typography variant="caption" sx={{ opacity: 0.9, minWidth: 0 }} noWrap>
                    {t("bpeWizard.discountLine", { pct: charge.descontoPct })}
                  </Typography>
                  <Typography variant="caption" fontWeight={700} sx={{ flexShrink: 0 }}>
                    {`− ${formatPrice(charge.descontoValor)}`}
                  </Typography>
                </Stack>
              )}
            </Stack>
          )}
        </Paper>
      )}

      <Typography variant="subtitle2" fontWeight={700}>
        {t("bpeWizard.paymentMethod")}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.25 }}>
        {t("bpeWizard.paymentHintShort")}
      </Typography>

      {paymentMethods.length === 0 ? (
        <Alert severity="warning">{t("bpeWizard.noPaymentMethods")}</Alert>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
            gap: 1,
            mb: 1.5,
          }}
        >
          {paymentMethods.map((pm) => {
            const selected = String(pm.id) === String(selectedPaymentId);
            const { Icon, desc, instant } = paymentVisual(pm, t);
            const surcharge = parsePercent(pm.acrescimo);
            const discount = parsePercent(pm.desconto);
            return (
              <ButtonBase
                key={pm.id}
                onClick={() => onSelectPayment(String(pm.id))}
                focusRipple
                sx={{ borderRadius: 2.5, textAlign: "left", display: "block", width: "100%" }}
              >
                <Paper
                  variant="outlined"
                  sx={(theme) => ({
                    position: "relative",
                    p: 1.25,
                    borderRadius: 2.5,
                    height: "100%",
                    borderWidth: 2,
                    borderColor: selected ? "primary.main" : "divider",
                    bgcolor: selected ? alpha(theme.palette.primary.main, 0.06) : "background.paper",
                    transition: "border-color .15s, background-color .15s, box-shadow .15s",
                    "&:hover": {
                      borderColor: selected ? "primary.main" : alpha(theme.palette.primary.main, 0.5),
                      boxShadow: `0 4px 14px -10px ${alpha(theme.palette.common.black, 0.5)}`,
                    },
                  })}
                >
                  {selected && (
                    <CheckCircleIcon
                      color="primary"
                      sx={{ position: "absolute", top: 8, right: 8, fontSize: 20 }}
                    />
                  )}
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Box
                      sx={(theme) => ({
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: selected ? "primary.main" : "text.secondary",
                        bgcolor: alpha(
                          selected ? theme.palette.primary.main : theme.palette.text.primary,
                          selected ? 0.12 : 0.06
                        ),
                      })}
                    >
                      <Icon fontSize="small" />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1, pr: selected ? 2.5 : 0 }}>
                      <Typography variant="body2" fontWeight={700} noWrap>
                        {pm.nome}
                      </Typography>
                      {desc && (
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                          {instant ? (
                            <BoltOutlinedIcon sx={{ fontSize: 13, color: "success.main" }} />
                          ) : (
                            <ScheduleOutlinedIcon sx={{ fontSize: 13, color: "text.disabled" }} />
                          )}
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {desc}
                          </Typography>
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                  {(surcharge > 0 || discount > 0) && (
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                      {surcharge > 0 && (
                        <Chip
                          size="small"
                          label={t("bpeWizard.surchargeChip", { pct: surcharge })}
                          sx={{
                            height: 20,
                            fontWeight: 700,
                            fontSize: "0.68rem",
                            bgcolor: (theme) => alpha(theme.palette.warning.main, 0.15),
                            color: "warning.dark",
                          }}
                        />
                      )}
                      {discount > 0 && (
                        <Chip
                          size="small"
                          label={t("bpeWizard.discountChip", { pct: discount })}
                          sx={{
                            height: 20,
                            fontWeight: 700,
                            fontSize: "0.68rem",
                            bgcolor: (theme) => alpha(theme.palette.success.main, 0.15),
                            color: "success.dark",
                          }}
                        />
                      )}
                    </Stack>
                  )}
                </Paper>
              </ButtonBase>
            );
          })}
        </Box>
      )}

      {isRedeCardPayment(selectedPayment) && (
        <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.grey[500], 0.04) }}>
          <BPeRedeCardForm
            value={redeCard}
            onChange={onRedeCardChange}
            disabled={loading}
            paymentMethod={selectedPayment}
          />
        </Paper>
      )}
    </Box>
  );
}
